import * as THREE from 'three';

/**
 * Compose the sketch overlay's world matrix from the scene-rotation pivot
 * and an optional physics frame delta:
 *
 *   world = pivotMatrixWorld * (T(delta.position) * R(delta.quaternion))
 *
 * The physics frame delta is the chair compound body's current pose relative
 * to its pose at body creation (see PhysicsWorld.getCompoundBodyDelta). The
 * overlay frame group is posed with exactly this delta, so inverting this
 * matrix maps a world-space pointer ray into the plane's LOCAL sketch frame
 * even while the chair is mid-simulation.
 *
 * @param {THREE.Matrix4|null} pivotMatrixWorld - Rotation pivot world matrix (or null)
 * @param {{position: THREE.Vector3, quaternion: THREE.Quaternion}|null} frameDelta - Physics delta (or null)
 * @returns {THREE.Matrix4} Composed matrix (identity when both are null)
 */
export function composeFrameMatrix(pivotMatrixWorld, frameDelta) {
    const matrix = pivotMatrixWorld
        ? new THREE.Matrix4().copy(pivotMatrixWorld)
        : new THREE.Matrix4();

    if (frameDelta && frameDelta.position && frameDelta.quaternion) {
        const deltaMatrix = new THREE.Matrix4().compose(
            frameDelta.position,
            frameDelta.quaternion,
            new THREE.Vector3(1, 1, 1)
        );
        matrix.multiply(deltaMatrix);
    }

    return matrix;
}

/**
 * PlaneIntersector - Ray casting from mouse/screen to 3D planes
 *
 * Converts 2D screen coordinates to 3D world coordinates on slice planes.
 * Critical for unified 3D view where drawing happens directly on 3D planes.
 */
export class PlaneIntersector {
    constructor() {
        this.raycaster = new THREE.Raycaster();
    }

    /**
     * Get intersection point of mouse ray with a plane
     *
     * @param {number} mouseX - Mouse X position (screen coordinates)
     * @param {number} mouseY - Mouse Y position (screen coordinates)
     * @param {THREE.Plane} plane - Plane to intersect with
     * @param {THREE.Camera} camera - Camera for ray casting
     * @param {HTMLElement} canvas - Canvas element for coordinate conversion
     * @returns {THREE.Vector3|null} Intersection point in world coordinates, or null if no intersection
     */
    static getPlaneIntersection(mouseX, mouseY, plane, camera, canvas, pivot = null, frameDelta = null) {
        const rect = canvas.getBoundingClientRect();

        // Convert mouse position to NDC (Normalized Device Coordinates: -1 to 1)
        const ndcX = ((mouseX - rect.left) / rect.width) * 2 - 1;
        const ndcY = -((mouseY - rect.top) / rect.height) * 2 + 1;

        // Create raycaster from camera through mouse position
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);

        // The scene rotates via a pivot group while the base planes are
        // defined in unrotated (pivot-local) coordinates. While physics is
        // playing the chair (and its sketch overlay frame) is additionally
        // moved by the compound body's frame delta. Transform the ray into
        // that local space so clicks land on the plane the user SEES.
        // The returned intersection is then in plane-local (sketch) space -
        // drawing coordinates are unchanged, only the ray moves.
        if (pivot || frameDelta) {
            if (pivot) pivot.updateMatrixWorld(true);
            const inverse = composeFrameMatrix(pivot ? pivot.matrixWorld : null, frameDelta).invert();
            raycaster.ray.applyMatrix4(inverse);
        }

        // Intersect ray with plane
        const intersection = new THREE.Vector3();
        const result = raycaster.ray.intersectPlane(plane, intersection);

        return result; // Returns Vector3 or null
    }

    /**
     * Get intersection with the nearest base plane from a set of planes
     *
     * @param {number} mouseX - Mouse X position
     * @param {number} mouseY - Mouse Y position
     * @param {Array<Object>} basePlanes - Array of base plane objects with position and normal
     * @param {THREE.Camera} camera - Camera for ray casting
     * @param {HTMLElement} canvas - Canvas element
     * @returns {{plane: Object, intersection: THREE.Vector3, index: number}|null} Nearest plane, intersection point, and index
     */
    static getNearestPlaneIntersection(mouseX, mouseY, basePlanes, camera, canvas) {
        let nearestDistance = Infinity;
        let nearestResult = null;

        basePlanes.forEach((basePlane, index) => {
            // Create Three.js plane from base plane
            const plane = new THREE.Plane(
                new THREE.Vector3(0, 0, 1), // Normal (Z-axis for XY planes)
                -basePlane.position.z       // Distance from origin
            );

            const intersection = PlaneIntersector.getPlaneIntersection(
                mouseX,
                mouseY,
                plane,
                camera,
                canvas
            );

            if (intersection) {
                // Calculate distance from camera to intersection
                const distance = intersection.distanceTo(camera.position);

                if (distance < nearestDistance) {
                    nearestDistance = distance;
                    nearestResult = {
                        plane: basePlane,
                        intersection: intersection,
                        index: index,
                        threeJsPlane: plane
                    };
                }
            }
        });

        return nearestResult;
    }

    /**
     * Get intersections with all selected base planes
     *
     * @param {number} mouseX - Mouse X position
     * @param {number} mouseY - Mouse Y position
     * @param {Array<Object>} basePlanes - Array of all base planes
     * @param {Set<number>} selectedIndices - Set of selected plane indices
     * @param {THREE.Camera} camera - Camera for ray casting
     * @param {HTMLElement} canvas - Canvas element
     * @returns {Array<{plane: Object, intersection: THREE.Vector3, index: number}>} Array of intersections
     */
    static getSelectedPlaneIntersections(mouseX, mouseY, basePlanes, selectedIndices, camera, canvas, pivot = null, frameDelta = null) {
        const results = [];

        selectedIndices.forEach(index => {
            const basePlane = basePlanes[index];

            // Create Three.js plane
            const plane = new THREE.Plane(
                new THREE.Vector3(0, 0, 1), // Normal
                -basePlane.position.z       // Distance
            );

            const intersection = PlaneIntersector.getPlaneIntersection(
                mouseX,
                mouseY,
                plane,
                camera,
                canvas,
                pivot,
                frameDelta
            );

            if (intersection) {
                results.push({
                    plane: basePlane,
                    intersection: intersection,
                    index: index,
                    threeJsPlane: plane
                });
            }
        });

        return results;
    }

    /**
     * Convert world coordinates to plane-local coordinates
     *
     * @param {THREE.Vector3} worldPos - World position
     * @param {Object} basePlane - Base plane with position and rotation
     * @returns {{x: number, y: number}} Plane-local 2D coordinates
     */
    static worldToPlaneCoordinates(worldPos, basePlane) {
        // For XY planes at different Z positions, local coords are just X and Y
        // (assuming no rotation for now)
        return {
            x: worldPos.x - basePlane.position.x,
            y: worldPos.y - basePlane.position.y
        };
    }

    /**
     * Convert plane-local coordinates to world coordinates
     *
     * @param {number} x - Plane-local X coordinate
     * @param {number} y - Plane-local Y coordinate
     * @param {Object} basePlane - Base plane with position and rotation
     * @returns {THREE.Vector3} World position
     */
    static planeToWorldCoordinates(x, y, basePlane) {
        // For XY planes at different Z positions
        return new THREE.Vector3(
            x + basePlane.position.x,
            y + basePlane.position.y,
            basePlane.position.z
        );
    }

    /**
     * Check if a point is within the bounds of a plane
     *
     * @param {THREE.Vector3} point - Point to check
     * @param {Object} basePlane - Base plane with bounds
     * @param {number} maxDistance - Maximum distance from plane center (optional)
     * @returns {boolean} True if point is within bounds
     */
    static isPointInPlaneBounds(point, basePlane, maxDistance = 10.0) {
        // Convert to plane coordinates
        const planeCoords = PlaneIntersector.worldToPlaneCoordinates(point, basePlane);

        // Check distance from plane center
        const distanceFromCenter = Math.sqrt(
            planeCoords.x * planeCoords.x +
            planeCoords.y * planeCoords.y
        );

        return distanceFromCenter <= maxDistance;
    }

    /**
     * Get ray direction for debugging/visualization
     *
     * @param {number} mouseX - Mouse X position
     * @param {number} mouseY - Mouse Y position
     * @param {THREE.Camera} camera - Camera for ray casting
     * @param {HTMLElement} canvas - Canvas element
     * @returns {{origin: THREE.Vector3, direction: THREE.Vector3}} Ray origin and direction
     */
    static getRay(mouseX, mouseY, camera, canvas) {
        const rect = canvas.getBoundingClientRect();

        const ndcX = ((mouseX - rect.left) / rect.width) * 2 - 1;
        const ndcY = -((mouseY - rect.top) / rect.height) * 2 + 1;

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);

        return {
            origin: raycaster.ray.origin.clone(),
            direction: raycaster.ray.direction.clone()
        };
    }

    /**
     * Instance method for getting plane intersection (uses internal raycaster)
     *
     * @param {number} mouseX - Mouse X position
     * @param {number} mouseY - Mouse Y position
     * @param {THREE.Plane} plane - Plane to intersect
     * @param {THREE.Camera} camera - Camera
     * @param {HTMLElement} canvas - Canvas element
     * @returns {THREE.Vector3|null} Intersection point or null
     */
    getIntersection(mouseX, mouseY, plane, camera, canvas) {
        const rect = canvas.getBoundingClientRect();

        const ndcX = ((mouseX - rect.left) / rect.width) * 2 - 1;
        const ndcY = -((mouseY - rect.top) / rect.height) * 2 + 1;

        this.raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);

        const intersection = new THREE.Vector3();
        return this.raycaster.ray.intersectPlane(plane, intersection);
    }
}
